import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useEffect, useRef } from 'react'
import { toastError } from '../components/FormComponents/Toasts'
import axiosService from '../utils/AxiosService'
import qs from 'qs'
import Authentication from '../models/Authentication'

export const SearchContext = React.createContext({})

export const defaultSearch = () => ({
  selectedFilters: [],
  selectedCountries: [],
  selectedCities: [],
  selectedAudienceCities: [],
  selectedAudienceCountries: [],
  selectedAudienceAgeGroups: [],
  selectedAgeGroups: [],
  selectedTags: [],
  provider: 'All',
  username: '',
  tag_names: '',
  min_reach: 1000,
  max_reach: 1_000_000,
  min_engagement_rate: 0,
  country: [],
  city: [],
  tags: [],
  gender: new Set([]),
  age_group: [],
  audience_gender: '',
  audience_countries: [],
  audience_cities: [],
  audience_age_groups: [],
  min_audience_country_percentage: 25,
  reachbird_approved: false,
  page: 1,
  order: 'reach'
})

export default observer(function SearchProvider(props) {
  const latestRequest = useRef(0)

  const searchState = useLocalObservable(() => {
    const savedSearch = JSON.parse(localStorage.getItem('searchState')) || {}

    if (savedSearch.gender) {
      savedSearch.gender = new Set(savedSearch.gender)
    }

    return {
      authentications: [],
      totalCount: 0,
      campaigns: [],
      filtersOpen: false,
      toggleFiltersOpen: () => searchState.filtersOpen = !searchState.filtersOpen,
      newAuthenticationModalOpen: false,
      toggleNewAuthenticationModal: () => searchState.newAuthenticationModalOpen = !searchState.newAuthenticationModalOpen,
      newInfluencerModalOpen: false,
      toggleNewInfluencerModal: () => searchState.newInfluencerModalOpen = !searchState.newInfluencerModalOpen,
      waitingListModalOpen: false,
      toggleWaitingListModal: () => searchState.waitingListModalOpen = !searchState.waitingListModalOpen,
      tagsModalOpen: false,
      toggleTagsModal: () => searchState.tagsModalOpen = !searchState.tagsModalOpen,
      tagsVersion: 0,
      bumpTagsVersion: () => searchState.tagsVersion += 1,
      loading: false,
      search: {
        ...defaultSearch(),
        ...savedSearch
      },

      resetSearch: () => {
        Object.assign(searchState.search, defaultSearch())
      }
    }
  })

  useEffect(() => {
    searchState.search.page = 1

    const searchToSave = {
      ...searchState.search,
      gender: Array.from(searchState.search.gender)
    }

    localStorage.setItem('searchState', JSON.stringify(searchToSave))

    fetchAuthentications({ append: false })
  }, [
    searchState.search.order,
    searchState.search.provider,
    searchState.search.username,
    searchState.search.min_reach,
    searchState.search.max_reach,
    searchState.search.min_engagement_rate,
    searchState.search.country,
    searchState.search.city,
    searchState.search.tags,
    searchState.search.gender.size,
    searchState.search.age_group,
    searchState.search.audience_gender,
    searchState.search.audience_countries.length,
    searchState.search.min_audience_country_percentage,
    searchState.search.audience_cities.length,
    searchState.search.audience_age_groups.length,
    searchState.search.selectedFilters.length,
    searchState.search.tag_names,
    searchState.search.reachbird_approved
  ])

  useEffect(() => {
    if(searchState.search.page === 1) return
    if(searchState.authentications.length >= searchState.totalCount) return
    fetchAuthentications({ append: true })
  }, [searchState.search.page])

  useEffect(() => {
    getCampaigns()
  }, [])

  const getCampaigns = () => {
    axiosService.get('/api/project_manager/campaigns')
    .then(campaigns => searchState.campaigns = campaigns)
    .catch(({ data: { error } }) => toastError(error))
  }

  const fetchAuthentications = ({ append }) => {
    const request = ++latestRequest.current
    const isLatest = () => request === latestRequest.current

    searchState.loading = true
    axiosService.get(`/api/project_manager/authentication_search.json?${qs.stringify({ search: getParams() }, { arrayFormat: 'brackets' })}`)
    .then(response => {
      if (!isLatest()) return
      const authentications = response.authentications.map(authentication => new Authentication(authentication))
      if (append) {
        searchState.authentications.push(...authentications)
      } else {
        searchState.authentications = authentications
        searchState.totalCount = response.total_count
      }
    })
    .catch(({ data: { error } }) => isLatest() && toastError(error))
    .finally(() => {
      setTimeout(() => { if (isLatest()) searchState.loading = false }, 500)
    })
  }

  const getParams = () => {
    return {
      min_reach: searchState.search.min_reach,
      max_reach: searchState.search.max_reach,
      country: searchState.search.country,
      city: searchState.search.city,
      tags: searchState.search.tags,
      age_group: searchState.search.age_group,
      audience_countries: searchState.search.audience_countries,
      audience_cities: searchState.search.audience_cities,
      audience_age_groups: searchState.search.audience_age_groups,
      audience_gender: searchState.search.audience_gender,
      min_audience_country_percentage: searchState.search.min_audience_country_percentage,
      min_engagement_rate: searchState.search.min_engagement_rate,
      gender: Array.from(searchState.search.gender),
      provider: searchState.search.provider,
      username: searchState.search.username,
      order: searchState.search.order,
      page: searchState.search.page,
      tag_names: searchState.search.tag_names,
      reachbird_approved: searchState.search.reachbird_approved
    }
  }

  return (
    <SearchContext.Provider
      value={{
        searchState,
        search: searchState.search,
        authentications: searchState.authentications,
        totalCount: searchState.totalCount,
        loading: searchState.loading,
        hasMore: searchState.authentications.length < searchState.totalCount,
        campaigns: searchState.campaigns,
        filtersOpen: searchState.filtersOpen,
        toggleFiltersOpen: searchState.toggleFiltersOpen,
        newAuthenticationModalOpen: searchState.newAuthenticationModalOpen,
        toggleNewAuthenticationModal: searchState.toggleNewAuthenticationModal,
        newInfluencerModalOpen: searchState.newInfluencerModalOpen,
        toggleNewInfluencerModal: searchState.toggleNewInfluencerModal,
        waitingListModalOpen: searchState.waitingListModalOpen,
        toggleWaitingListModal: searchState.toggleWaitingListModal,
        tagsModalOpen: searchState.tagsModalOpen,
        toggleTagsModal: searchState.toggleTagsModal,
        tagsVersion: searchState.tagsVersion,
        bumpTagsVersion: searchState.bumpTagsVersion,
        resetSearch: searchState.resetSearch
      }}
    >
      {props.children}
    </SearchContext.Provider>
  )
})
